import { useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient, queryKeys } from './query-client';
import { stampFunnelCohort } from './gtm-funnel';

const LOCAL_BUMPS_KEY = 'orion-web:conversation-bumps';

// Local optimistic bumps persisted across page reloads. bumpConversation writes
// the new timestamp here, and the query merges it back in so a freshly-sent
// conversation stays on top even after a hard refresh that lands before the
// server-side updated_date catches up. Entries clear the moment the server's
// timestamp catches up, so the map never holds stale ghosts.
function readLocalBumps(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(LOCAL_BUMPS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function writeLocalBumps(next: Record<string, string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LOCAL_BUMPS_KEY, JSON.stringify(next));
  } catch {
    // quota / private-mode — the cache still holds the bump for the session
  }
}

function recordLocalBump(id: string, isoTimestamp: string): void {
  if (!id) return;
  const next = readLocalBumps();
  next[id] = isoTimestamp;
  writeLocalBumps(next);
}

export type Conversation = {
  id?: string;
  name?: string;
  createdDate?: string;
  updatedDate?: string;
  source?: string;
  active?: boolean;
  // WEB | APP | WHATSAPP | SMS | AGENT (the sidebar channel set — see
  // pages/api/conversations.ts). WHATSAPP/SMS/AGENT threads are read-only on web:
  // badged in the sidebar, locked composer in the chat.
  communicationChannel?: string;
  // WhatsApp GROUP conversation (always false until group metadata ships).
  isWaGroup?: boolean;
  // The user pinned this conversation. The API returns pinned rows first (two queries — see
  // pages/api/conversations.ts), so the sidebar renders the order it is given.
  pinned?: boolean;
  // A rolling one-line summary of what the conversation has been about, written server-side. Rendered
  // above the composer in the chat, not in the sidebar row. Absent until the conversation has had enough
  // turns to be worth summarising.
  recap?: string;
  // EXTERNAL_AGENT only: the external agent platform's name and the link back to it, for the locked bar's
  // "Continue in {platform}" CTA. Already sanitized server-side (absolute https or absent), so its
  // presence means there is a safe CTA to render.
  agentPlatform?: { name: string; url?: string };
};

export type ConversationsState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  // `hasMore` = the server had more pages beyond the fetched window.
  | { kind: 'ready'; conversations: Conversation[]; hasMore?: boolean };

export type ConversationSection = { title: string; items: Conversation[] };

type ConversationsData = { conversations: Conversation[]; hasMore: boolean };

function getCached(): ConversationsData | undefined {
  return queryClient.getQueryData<ConversationsData>(queryKeys.conversations);
}

function setCached(next: ConversationsData): void {
  queryClient.setQueryData<ConversationsData>(queryKeys.conversations, next);
}

const PENDING_CREATE_WINDOW_MS = 60 * 1000;

async function fetchConversations(): Promise<ConversationsData> {
  const prev = getCached();
  const res = await apiFetch(`${API_BASE}/api/conversations`, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`${res.status} ${body || res.statusText}`);
  }
  const data = (await res.json()) as { conversations?: Conversation[]; hasMore?: boolean };

  // Merge server rows with any local optimistic `updatedDate` — from the last
  // fetched list AND from localStorage — so a bumped conversation doesn't slide
  // back to its old position before server-side persistence catches up. Keep the
  // local timestamp whenever it's newer; drop the localStorage entry once the
  // server catches up so the map stays small.
  const localUpdatedById = new Map<string, string>();
  if (prev) {
    for (const c of prev.conversations) {
      if (c.id && c.updatedDate) localUpdatedById.set(c.id, c.updatedDate);
    }
  }
  const persistedBumps = readLocalBumps();
  for (const [id, ts] of Object.entries(persistedBumps)) {
    const inMemory = localUpdatedById.get(id);
    if (!inMemory || new Date(ts).getTime() > new Date(inMemory).getTime()) {
      localUpdatedById.set(id, ts);
    }
  }
  const remainingBumps: Record<string, string> = {};
  // VMR queryConversations returns rows in an unspecified order — sort newest-first
  // by updatedDate so the sidebar buckets read newest-first within each section.
  let active = (data.conversations || [])
    .filter((c) => c.active !== false)
    .map((c) => {
      if (!c.id) return c;
      const local = localUpdatedById.get(c.id);
      if (!local) return c;
      const serverTime = c.updatedDate ? new Date(c.updatedDate).getTime() : 0;
      const localTime = new Date(local).getTime();
      if (localTime > serverTime) {
        if (persistedBumps[c.id]) remainingBumps[c.id] = local;
        return { ...c, updatedDate: local };
      }
      return c;
    })
    .sort((a, b) => {
      const aTime = new Date(a.updatedDate || a.createdDate || 0).getTime();
      const bTime = new Date(b.updatedDate || b.createdDate || 0).getTime();
      return bTime - aTime;
    });

  // Preserve a just-created conversation the server hasn't indexed yet: re-add
  // only VERY recently created rows still missing from the response, within a
  // short window. Once the server returns the row it comes through `active`
  // (deduped by id); a deleted row is never re-added.
  if (prev) {
    const serverIds = new Set(active.map((c) => c.id).filter((id): id is string => !!id));
    const now = Date.now();
    const pendingCreates = prev.conversations.filter(
      (c) => c.id && !serverIds.has(c.id) && c.createdDate && now - new Date(c.createdDate).getTime() < PENDING_CREATE_WINDOW_MS,
    );
    if (pendingCreates.length > 0) {
      active = [...pendingCreates, ...active].sort((a, b) => {
        const aTime = new Date(a.updatedDate || a.createdDate || 0).getTime();
        const bTime = new Date(b.updatedDate || b.createdDate || 0).getTime();
        return bTime - aTime;
      });
    }
  }

  if (Object.keys(persistedBumps).length > 0 || Object.keys(remainingBumps).length > 0) {
    writeLocalBumps(remainingBumps);
  }
  return { conversations: active, hasMore: data.hasMore === true };
}

async function ensureFetched(): Promise<ConversationsData | null> {
  try {
    return await queryClient.ensureQueryData({ queryKey: queryKeys.conversations, queryFn: fetchConversations });
  } catch {
    return null;
  }
}

// Resume target for reopening the chat with no remembered thread: the id of the
// most recently active conversation whose last activity is within `windowMs`,
// else null.
export async function getRecentConversationId(windowMs: number): Promise<string | null> {
  const data = await ensureFetched();
  if (!data) return null;
  const head = data.conversations.find((c) => !!c.id);
  if (!head?.id) return null;
  const ts = head.updatedDate || head.createdDate;
  const t = ts ? new Date(ts).getTime() : 0;
  if (!Number.isFinite(t) || t <= 0) return null;
  return Date.now() - t <= windowMs ? head.id : null;
}

// Move a conversation to the top and bump its updatedDate to now — mirrors
// mobile's bump-on-send so the sidebar reflects activity immediately, and
// persists the bump so a refresh before the server catches up still shows it on top.
export function bumpConversation(id: string): void {
  if (!id) return;
  const now = new Date().toISOString();
  recordLocalBump(id, now);
  const prev = getCached();
  if (!prev) return;
  const target = prev.conversations.find((c) => c.id === id);
  if (!target) return;
  const bumped: Conversation = { ...target, updatedDate: now };
  const rest = prev.conversations.filter((c) => c.id !== id);
  setCached({ ...prev, conversations: [bumped, ...rest] });
}

// Deletes on the server (a recoverable trash-bin delete) then drops the row from
// the cache. The row only disappears after the server confirms, so a failed
// delete never lies about the list.
/** The row a chat outside the list page is drawn from (`useConversationRow`). The optimistic list patch
 *  above cannot reach it — the list does not hold that conversation — so the row is dropped and refetched,
 *  or the header would keep showing the old title / pin state after a write. */
function invalidateConversationRow(id: string): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.conversation(id) });
}

export async function deleteConversation(id: string): Promise<boolean> {
  if (!id) return false;
  try {
    const res = await apiFetch(`${API_BASE}/api/conversations/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!res.ok) return false;
  } catch {
    return false;
  }
  const bumps = readLocalBumps();
  if (bumps[id]) {
    delete bumps[id];
    writeLocalBumps(bumps);
  }
  const prev = getCached();
  if (prev) {
    setCached({ ...prev, conversations: prev.conversations.filter((c) => c.id !== id) });
  }
  invalidateConversationRow(id);
  return true;
}

/**
 * Pin or unpin, optimistically. The list is ordered pinned-first by the API (two queries on the pin axis),
 * so the local cache has to be RE-SORTED, not just flagged — flipping the boolean alone would leave the row
 * where it was and read as "the pin did nothing".
 *
 * Rolls back on failure, per the house style for optimistic writes.
 */

export async function pinConversation(id: string, pinned: boolean): Promise<boolean> {
  if (!id) return false;
  const before = getCached();
  if (before) setCached({ ...before, conversations: sortPinnedFirst(before.conversations.map((c) => (c.id === id ? { ...c, pinned } : c))) });
  try {
    const res = await apiFetch(`${API_BASE}/api/conversations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ pinned }),
    });
    if (!res.ok) throw new Error(String(res.status));
  } catch {
    if (before) setCached(before);
    return false;
  }
  invalidateConversationRow(id);
  return true;
}

/**
 * Rename, optimistically. The server also stamps `userRenamed`, which is what stops the auto-namer from
 * replacing a title the user chose — so a rename here is permanent in a way an assistant-generated one is not.
 *
 * Rolls back on failure, per the house style for optimistic writes.
 */
export async function renameConversation(id: string, name: string): Promise<boolean> {
  const next = name.trim();
  if (!id || !next) return false;
  const before = getCached();
  if (before) setCached({ ...before, conversations: before.conversations.map((c) => (c.id === id ? { ...c, name: next } : c)) });
  try {
    const res = await apiFetch(`${API_BASE}/api/conversations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name: next }),
    });
    if (!res.ok) throw new Error(String(res.status));
  } catch {
    if (before) setCached(before);
    return false;
  }
  invalidateConversationRow(id);
  return true;
}

/** Pinned rows first, each group keeping the server's recency order. A stable partition, so two pinned rows
 *  never swap places just because one was re-flagged. */
function sortPinnedFirst(list: Conversation[]): Conversation[] {
  return [...list.filter((c) => c.pinned), ...list.filter((c) => !c.pinned)];
}

export function useConversations(enabled: boolean): {
  state: ConversationsState;
  prepend: (c: Conversation) => void;
  refresh: () => Promise<Conversation[] | null>;
} {
  const { data, status, error, refetch } = useQuery({
    queryKey: queryKeys.conversations,
    queryFn: fetchConversations,
    enabled,
    // The shared QueryClient defaults to retry:false, which latches this query to `error` on a single
    // failed read — and the onboarding-entry decision (lib/use-onboarding-entry.ts) treats a non-`ready`
    // list as "unresolved", holding the home on the loader with no recovery. Give it a BOUNDED retry so a
    // transient blip self-heals instead of stranding the home; capped, so a genuine outage still settles.
    retry: 2,
    retryDelay: 1000,
  });

  // GTM activation-funnel cohort. The funnel is only about users whose journey starts
  // after it ships, so a browser is stamped 'new'/'existing' the first time we can see
  // whether the account already has chat history — and this hook is that moment. Every
  // surface that can send a message mounts it (AppFrame, ChatSurface, FloatingChat) and
  // they share one query. Idempotent: the first answer wins forever, so a new user creating
  // their first thread can't restamp themselves as existing. See lib/gtm-funnel.ts.
  //
  // A send that beat this query would be dropped from the funnel (the gate fails closed).
  // In practice it can't: the composer needs the shell, and the one path that sends without
  // a human keypress — useAutosend — only runs off an in-app handoff (Pulse Edit, Task
  // chat-about, Knowledge Edit, the home launcher), all of which require an account that
  // already has content, i.e. a user who is not in this cohort anyway.
  useEffect(() => {
    if (!data) return;
    stampFunnelCohort(data.conversations.length === 0);
  }, [data]);

  // Tab-visibility fallback for cross-tab/cross-device sync. The duplexer covers
  // the realtime case in prod but is HTTPS-gated (no-op on local dev) and can drop
  // events on network blips. Tabbing back forces a refetch. Cheap: one listener.
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [enabled, refetch]);

  // Prefer showing data even when a background refetch errored — a failed refresh
  // must not clobber a good list.
  const state: ConversationsState = data
    ? { kind: 'ready', conversations: data.conversations, hasMore: data.hasMore }
    : status === 'error' && error instanceof Error
      ? { kind: 'error', message: error.message }
      : { kind: 'loading' };

  const prepend = useCallback((c: Conversation) => {
    const prev = getCached();
    if (!prev) {
      setCached({ conversations: [c], hasMore: false });
      return;
    }
    if (c.id && prev.conversations.some((x) => x.id === c.id)) return;
    setCached({ ...prev, conversations: [c, ...prev.conversations] });
  }, []);

  const refresh = useCallback(async () => {
    const result = await refetch();
    return result.data?.conversations ?? null;
  }, [refetch]);

  return { state, prepend, refresh };
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Mirrors mobile's chat-history grouping
// (mobile-platform/.../menu/history/chat-sections-utils.ts).
export function buildConversationSections(conversations: Conversation[]): ConversationSection[] {
  const now = Date.now();
  const startOfToday = new Date().setHours(0, 0, 0, 0);
  const sevenDaysAgo = now - 7 * DAY_MS;
  const thirtyDaysAgo = now - 30 * DAY_MS;

  const buckets: Record<string, Conversation[]> = {
    today: [],
    week: [],
    month: [],
    older: [],
  };

  for (const c of conversations) {
    const ts = c.updatedDate || c.createdDate;
    const t = ts ? new Date(ts).getTime() : 0;
    if (!Number.isFinite(t) || t === 0) {
      buckets.older.push(c);
    } else if (t >= startOfToday) {
      buckets.today.push(c);
    } else if (t >= sevenDaysAgo) {
      buckets.week.push(c);
    } else if (t >= thirtyDaysAgo) {
      buckets.month.push(c);
    } else {
      buckets.older.push(c);
    }
  }

  const sections: ConversationSection[] = [];
  if (buckets.today.length) sections.push({ title: 'Today', items: buckets.today });
  if (buckets.week.length) sections.push({ title: 'Last 7 days', items: buckets.week });
  if (buckets.month.length) sections.push({ title: 'Last 30 days', items: buckets.month });
  if (buckets.older.length) sections.push({ title: 'Older', items: buckets.older });
  return sections;
}

export function formatConversationDate(value: string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(
    [],
    sameYear ? { month: 'short', day: 'numeric' } : { year: 'numeric', month: 'short', day: 'numeric' },
  );
}
